import * as os from 'node:os';

/**
 * Collects Node.js process metrics (memory, CPU, event loop lag) and reports
 * them periodically to the BugCatch server-metrics ingest endpoint.
 *
 * Framework-agnostic - wrap it in any lifecycle hooks (NestJS, Express, etc.)
 *
 * @example NestJS
 * ```ts
 * \@Injectable()
 * export class BugCatchMetricsService implements OnModuleInit, OnModuleDestroy {
 *   private readonly reporter = new BugCatchServerReporter({
 *     dsn: process.env.BUGCATCH_DSN!,
 *   });
 *   onModuleInit()    { this.reporter.start(); }
 *   onModuleDestroy() { this.reporter.stop();  }
 * }
 * ```
 */
export interface ServerReporterOptions {
  /** DSN copied from the BugCatch dashboard (same as used for error capture). */
  dsn: string;
  /**
   * How often to send a snapshot in milliseconds.
   * @default 30_000
   */
  reportInterval?: number;
  /**
   * Identifier for the reporting server/instance, shown in the dashboard and
   * alert emails so you can tell which process a snapshot came from.
   * @default os.hostname()
   */
  instanceId?: string;
  /** Print debug logs. @default false */
  debug?: boolean;
}

function parseDsnForServerMetrics(dsn: string): string {
  let url: URL;
  try {
    url = new URL(dsn);
  } catch {
    throw new Error(`[BugCatch/ServerReporter] Invalid DSN: "${dsn}"`);
  }

  const sdkKey = url.searchParams.get('key');
  if (!sdkKey) throw new Error(`[BugCatch/ServerReporter] DSN missing "key" param: "${dsn}"`);

  const segments = url.pathname.split('/').filter(Boolean);
  const projectId = segments[segments.length - 1];
  if (!projectId) throw new Error(`[BugCatch/ServerReporter] Cannot extract projectId from DSN: "${dsn}"`);

  return `${url.origin}${url.pathname}/server-metrics?key=${sdkKey}`;
}

function measureEventLoopLag(): Promise<number> {
  const start = Date.now();
  return new Promise<number>((resolve) => {
    setImmediate(() => resolve(Date.now() - start));
  });
}

export class BugCatchServerReporter {
  private timer: ReturnType<typeof setInterval> | null = null;
  private readonly serverMetricsUrl: string;
  private readonly interval: number;
  private readonly instanceId: string;
  private readonly debug: boolean;
  private prevCpuUsage = process.cpuUsage();
  private prevCpuTime = Date.now();

  constructor(opts: ServerReporterOptions) {
    this.serverMetricsUrl = parseDsnForServerMetrics(opts.dsn);
    this.interval = opts.reportInterval ?? 30_000;
    this.instanceId = opts.instanceId ?? os.hostname();
    this.debug = opts.debug ?? false;
  }

  start(): void {
    if (this.timer) return;
    // send immediately, then on each interval
    void this.report();
    this.timer = setInterval(() => void this.report(), this.interval);
    // unref so the timer doesn't prevent the process from exiting naturally
    if (typeof this.timer.unref === 'function') this.timer.unref();
    this.log(`Started - reporting every ${this.interval}ms`);
  }

  stop(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
      this.log('Stopped');
    }
  }

  private async report(): Promise<void> {
    try {
      const mem = process.memoryUsage();
      const heapUsedMb  = +(mem.heapUsed  / 1024 / 1024).toFixed(2);
      const heapTotalMb = +(mem.heapTotal / 1024 / 1024).toFixed(2);
      const rssMb       = +(mem.rss       / 1024 / 1024).toFixed(2);
      const uptimeSeconds = Math.floor(process.uptime());

      // CPU delta over the last interval
      const now = Date.now();
      const elapsed = (now - this.prevCpuTime) * 1000; // µs
      const cpuDelta = process.cpuUsage(this.prevCpuUsage);
      const cpuPercent = elapsed > 0
        ? +((cpuDelta.user + cpuDelta.system) / elapsed * 100).toFixed(2)
        : null;
      this.prevCpuUsage = process.cpuUsage();
      this.prevCpuTime = now;

      const eventLoopLagMs = +(await measureEventLoopLag()).toFixed(2);

      const payload = {
        instance_id:       this.instanceId,
        heap_used_mb:      heapUsedMb,
        heap_total_mb:     heapTotalMb,
        rss_mb:            rssMb,
        cpu_percent:       cpuPercent,
        event_loop_lag_ms: eventLoopLagMs,
        uptime_seconds:    uptimeSeconds,
      };

      this.log('Sending snapshot', payload);

      const res = await fetch(this.serverMetricsUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        this.log('Server rejected snapshot -', res.status, await res.text().catch(() => ''));
      }
    } catch (err) {
      this.log('Failed to send snapshot -', (err as Error).message);
    }
  }

  private log(...args: unknown[]): void {
    if (this.debug) console.debug('[BugCatch/ServerReporter]', ...args);
  }
}
